import { isValidElement, useCallback, useMemo, useState } from "react";
import { faEllipsis } from "@fortawesome/pro-solid-svg-icons";
import { InteractionType, MessageDirection, Sentiment, sentimentLabels } from "@edgetier/watchtower-types";
import { Box } from "../../../box";
import { Group } from "../../../group";
import { Menu } from "../../../menu";
import { ActionIcon } from "../../../action-icon";
import { Icon } from "../../../icon";
import { MessageOptionsSentiment } from "./message-options-sentiment";
import { useAudioContext } from "../../audio-context";

/**
 * Renders the contextual action menu for a chat message, including sentiment toggles and tag management.
 * @param {boolean} props.visible - Whether the menu trigger icon is visible.
 * @param {boolean} props.hasFrustration - Whether the message has frustration sentiment.
 * @param {boolean} props.hasConfusion - Whether the message has confusion sentiment.
 * @param {boolean} props.hasGratitude - Whether the message has gratitude sentiment.
 * @param {boolean} props.hasPraise - Whether the message has praise sentiment.
 * @param {boolean} props.hasEmpathy - Whether the message has empathy sentiment.
 * @param {Function} props.onSentimentToggle - Callback invoked when a sentiment is toggled, or undefined if disabled.
 * @param {Date} props.sendDateTime - The timestamp of the message.
 * @param {MessageDirection} props.messageDirection - The direction of the message (inbound or outbound).
 * @param {InteractionType} props.interactionType - The type of interaction (chat, call, etc.).
 * @param {React.ReactNode} props.addTagButton - Optional button element for adding tags.
 * @param {Function} props.messageAudioSeek - Optional render function for the audio seek control.
 * @returns {JSX.Element | null} The MessageOptions component, or null if no actions are available.
 */
const MessageOptions = ({
    visible,
    hasFrustration,
    hasConfusion,
    hasGratitude,
    hasPraise,
    hasEmpathy,
    onSentimentToggle,
    sendDateTime,
    messageDirection,
    interactionType,
    addTagButton,
    messageAudioSeek,
}: {
    visible: boolean;
    hasFrustration?: boolean;
    hasConfusion?: boolean;
    hasGratitude?: boolean;
    hasPraise?: boolean;
    hasEmpathy?: boolean;
    onSentimentToggle: ((sentiment: Sentiment) => Promise<void>) | undefined;
    sendDateTime: Date;
    messageDirection: MessageDirection;
    interactionType: InteractionType;
    addTagButton?: React.ReactNode;
    messageAudioSeek?: (props: {
        sendDateTime: Date;
        messageDirection: MessageDirection;
        isVisible: boolean;
    }) => React.ReactNode;
}) => {
    const audioContext = useAudioContext();
    const [opened, setOpened] = useState(false);

    const hasAddTagButton = useMemo(() => isValidElement(addTagButton), [addTagButton]);
    const hasMessageSentimentOptions = useMemo(() => typeof onSentimentToggle !== "undefined", [onSentimentToggle]);
    const hasBothMenuOptions = useMemo(
        () => hasAddTagButton && hasMessageSentimentOptions,
        [hasAddTagButton, hasMessageSentimentOptions]
    );

    // We should only show the audio seek icon when the messageAudioSeek prop is provided and when the interaction is a
    // call. The position of the icon also changes depending on if the message is inbound or outbound.
    const isMessageAudioSeekAvailable = useMemo(() => {
        const isAvailable = messageAudioSeek && interactionType === InteractionType.Call && !audioContext?.error;
        const isBeforeMenu = messageDirection === MessageDirection.Outbound;
        return { isAvailable, isBeforeMenu };
    }, [audioContext?.error, interactionType, messageAudioSeek, messageDirection]);

    // If the interaction is a call, we show an extra icon next to the menu. In this case, we need to change the border
    // radius of the menu icon to fit correctly with the extra icon.
    const menuIconBorderRadius = useCallback(
        (radius: string) => {
            if (isMessageAudioSeekAvailable.isAvailable) {
                return messageDirection === MessageDirection.Outbound
                    ? `0 ${radius} ${radius} 0`
                    : `${radius} 0 0 ${radius}`;
            }
            return undefined;
        },
        [isMessageAudioSeekAvailable.isAvailable, messageDirection]
    );

    if (!hasAddTagButton && !hasMessageSentimentOptions) {
        return null;
    }

    return (
        <Box>
            <Group gap="0" wrap="nowrap">
                {isMessageAudioSeekAvailable.isAvailable &&
                    isMessageAudioSeekAvailable.isBeforeMenu &&
                    messageAudioSeek!({ sendDateTime, messageDirection, isVisible: visible })}
                <Menu
                    keepMounted
                    withinPortal={false}
                    opened={opened}
                    onOpen={() => setOpened(true)}
                    onClose={() => setOpened(false)}
                >
                    <Menu.Target data-testid="chat-actions-menu-target">
                        <ActionIcon
                            size="sm"
                            color="gray"
                            variant="default"
                            styles={(theme) => ({
                                root: {
                                    visibility: visible ? "visible" : "hidden",
                                    borderRadius: menuIconBorderRadius(theme.radius.md),
                                },
                            })}
                        >
                            <Icon icon={faEllipsis} />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown w={174} onMouseLeave={() => setOpened(false)}>
                        {hasAddTagButton && addTagButton}
                        {hasBothMenuOptions && <Menu.Divider />}
                        {hasMessageSentimentOptions &&
                            (
                                [
                                    [Sentiment.Frustration, hasFrustration],
                                    [Sentiment.Confusion, hasConfusion],
                                    [Sentiment.Gratitude, hasGratitude],
                                    [Sentiment.Praise, hasPraise],
                                    [Sentiment.Empathy, hasEmpathy],
                                ] as [Sentiment, boolean][]
                            )
                                .filter(([, value]) => typeof value !== "undefined")
                                .map(([sentiment, value]) => (
                                    <MessageOptionsSentiment
                                        key={`sentiment-toggler-${sentimentLabels[sentiment as Sentiment]}`}
                                        sentiment={sentiment}
                                        value={value}
                                        onSentimentToggle={onSentimentToggle}
                                    />
                                ))}
                    </Menu.Dropdown>
                </Menu>
                {isMessageAudioSeekAvailable.isAvailable &&
                    !isMessageAudioSeekAvailable.isBeforeMenu &&
                    messageAudioSeek!({ sendDateTime, messageDirection, isVisible: visible })}
            </Group>
        </Box>
    );
};

export { MessageOptions };
